import React, { useMemo, useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Switch } from '@/shared/components/ui/Switch';
import { Icon } from '@/shared/icons';
import { useStepsByFunnelId, useUpdateFunnel, useUpdateFunnelStep } from '../hooks';
import type { AlertClockConfig, AlertConfig, FunnelResponse, FunnelStepResponse } from '@troia-v3/types';
import { SYSTEM_ALERT_DEFAULTS } from '@troia-v3/types';

/**
 * Defaults do sistema com fallback local. Blindagem contra o dev server do Vite
 * servir um pré-bundle antigo de `@troia-v3/types` onde `SYSTEM_ALERT_DEFAULTS`
 * ainda não existe (const `undefined` em runtime, tsc verde) — issue conhecida
 * ao bumpar o types com o dev server vivo. Também serve de floor do produto.
 */
const ALERT_DEFAULTS: { inactivity: AlertClockConfig; timeInStep: AlertClockConfig } =
  SYSTEM_ALERT_DEFAULTS ?? {
    inactivity: { enabled: true, warningDays: 7, criticalDays: 14 },
    timeInStep: { enabled: true, warningDays: 14, criticalDays: 30 },
  };

/**
 * Aba "Alertas" do drawer de configuração de funis.
 *
 * Configura os dois "relógios" de inatividade (última atividade / tempo na
 * etapa) usados pelo backend para colorir os cards do kanban, com override
 * opcional por etapa e os 3 toggles de notificação (disparo é fase diferida).
 *
 * Consome `AlertConfig`/`AlertClockConfig`/`SYSTEM_ALERT_DEFAULTS` de
 * `@troia-v3/types` (>= 1.2.154). Sem mudança de backend — Plano 1 já entrega
 * os campos `Funnel.alertConfig`/`FunnelStep.alertConfig`.
 *
 * Ver plano: DOCS/superpowers/plans/2026-07-17-inactivity-alerts-ui.md (Task 1)
 */
export interface FunnelAlertsTabProps {
  funnel: FunnelResponse;
}

interface ClockFormState {
  enabled: boolean;
  warningDays: number;
  criticalDays: number;
}

interface NotificationsFormState {
  onWarning: boolean;
  onCritical: boolean;
  weeklyDigest: boolean;
}

interface StepClockDraft {
  warningDays: number;
  criticalDays: number;
}

interface StepOverrideDraft {
  inactivity: StepClockDraft;
  timeInStep: StepClockDraft;
}

const clockToForm = (clock: AlertClockConfig | undefined, fallback: AlertClockConfig): ClockFormState => ({
  enabled: clock?.enabled ?? fallback.enabled,
  warningDays: clock?.warningDays ?? fallback.warningDays,
  criticalDays: clock?.criticalDays ?? fallback.criticalDays,
});

/** `criticalDays > warningDays >= 1` — mesma validação do Zod no backend. */
const validateClockDays = (warningDays: number, criticalDays: number): string | null => {
  if (!Number.isFinite(warningDays) || warningDays < 1) {
    return 'O alerta deve ser de pelo menos 1 dia.';
  }
  if (!Number.isFinite(criticalDays) || criticalDays <= warningDays) {
    return 'O crítico deve ser maior que o alerta.';
  }
  return null;
};

const validateClock = (clock: ClockFormState): string | null => {
  if (!clock.enabled) return null;
  return validateClockDays(clock.warningDays, clock.criticalDays);
};

const buildSummaryText = (inactivity: ClockFormState, timeInStep: ClockFormState): string => {
  const parts: string[] = [];
  if (inactivity.enabled) {
    parts.push(`Inatividade alerta em ${inactivity.warningDays} dias, crítico em ${inactivity.criticalDays}`);
  }
  if (timeInStep.enabled) {
    parts.push(`Tempo na etapa alerta em ${timeInStep.warningDays} dias, crítico em ${timeInStep.criticalDays}`);
  }
  if (parts.length === 0) {
    return 'Nenhum relógio ativo — os cards não mostrarão avisos de inatividade.';
  }
  return `${parts.join(' · ')}.`;
};

const hasStepOverride = (step: FunnelStepResponse): boolean =>
  !!(step.alertConfig && (step.alertConfig.inactivity || step.alertConfig.timeInStep));

export const FunnelAlertsTab: React.FC<FunnelAlertsTabProps> = ({ funnel }) => {
  const { data: stepsData, isLoading: stepsLoading } = useStepsByFunnelId(funnel.id);
  const updateFunnel = useUpdateFunnel();
  const updateFunnelStep = useUpdateFunnelStep();

  const steps = useMemo(() => stepsData ?? [], [stepsData]);

  const [inactivity, setInactivity] = useState<ClockFormState>(() =>
    clockToForm(funnel.alertConfig?.inactivity, ALERT_DEFAULTS.inactivity)
  );
  const [timeInStep, setTimeInStep] = useState<ClockFormState>(() =>
    clockToForm(funnel.alertConfig?.timeInStep, ALERT_DEFAULTS.timeInStep)
  );
  const [notifications, setNotifications] = useState<NotificationsFormState>(() => ({
    onWarning: funnel.alertConfig?.notifications?.onWarning ?? false,
    onCritical: funnel.alertConfig?.notifications?.onCritical ?? false,
    weeklyDigest: funnel.alertConfig?.notifications?.weeklyDigest ?? false,
  }));

  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
  const [stepDraft, setStepDraft] = useState<StepOverrideDraft | null>(null);
  const [stepDraftErrors, setStepDraftErrors] = useState<{ inactivity?: string | null; timeInStep?: string | null }>({});

  const inactivityError = validateClock(inactivity);
  const timeInStepError = validateClock(timeInStep);
  const canSaveFunnelAlerts = !inactivityError && !timeInStepError;

  const summaryText = useMemo(() => buildSummaryText(inactivity, timeInStep), [inactivity, timeInStep]);

  const effectiveFunnelInactivity = funnel.alertConfig?.inactivity ?? ALERT_DEFAULTS.inactivity;
  const effectiveFunnelTimeInStep = funnel.alertConfig?.timeInStep ?? ALERT_DEFAULTS.timeInStep;

  const handleSaveFunnelAlerts = async () => {
    if (!canSaveFunnelAlerts) return;
    const alertConfig: AlertConfig = {
      inactivity: { enabled: inactivity.enabled, warningDays: inactivity.warningDays, criticalDays: inactivity.criticalDays },
      timeInStep: { enabled: timeInStep.enabled, warningDays: timeInStep.warningDays, criticalDays: timeInStep.criticalDays },
      notifications: { ...notifications },
    };
    try {
      await updateFunnel.mutateAsync({ id: funnel.id, data: { alertConfig } });
    } catch {
      // toast já tratado no hook useUpdateFunnel
    }
  };

  const handleTogglePersonalize = (step: FunnelStepResponse) => {
    if (expandedStepId === step.id) {
      setExpandedStepId(null);
      setStepDraft(null);
      setStepDraftErrors({});
      return;
    }
    const override = step.alertConfig;
    setStepDraft({
      inactivity: {
        warningDays: override?.inactivity?.warningDays ?? effectiveFunnelInactivity.warningDays,
        criticalDays: override?.inactivity?.criticalDays ?? effectiveFunnelInactivity.criticalDays,
      },
      timeInStep: {
        warningDays: override?.timeInStep?.warningDays ?? effectiveFunnelTimeInStep.warningDays,
        criticalDays: override?.timeInStep?.criticalDays ?? effectiveFunnelTimeInStep.criticalDays,
      },
    });
    setStepDraftErrors({});
    setExpandedStepId(step.id);
  };

  const handleSaveStepOverride = async (step: FunnelStepResponse) => {
    if (!stepDraft) return;
    const errInactivity = validateClockDays(stepDraft.inactivity.warningDays, stepDraft.inactivity.criticalDays);
    const errTimeInStep = validateClockDays(stepDraft.timeInStep.warningDays, stepDraft.timeInStep.criticalDays);
    if (errInactivity || errTimeInStep) {
      setStepDraftErrors({ inactivity: errInactivity, timeInStep: errTimeInStep });
      return;
    }
    const alertConfig: AlertConfig = {
      inactivity: { enabled: true, warningDays: stepDraft.inactivity.warningDays, criticalDays: stepDraft.inactivity.criticalDays },
      timeInStep: { enabled: true, warningDays: stepDraft.timeInStep.warningDays, criticalDays: stepDraft.timeInStep.criticalDays },
    };
    try {
      await updateFunnelStep.mutateAsync({ id: step.id, data: { alertConfig } });
      setExpandedStepId(null);
      setStepDraft(null);
      setStepDraftErrors({});
    } catch {
      // toast já tratado no hook useUpdateFunnelStep
    }
  };

  const handleRestoreStepDefault = async (step: FunnelStepResponse) => {
    try {
      // Objeto vazio → clocks ausentes → herda o funil (backend Zod aceita `{}`).
      await updateFunnelStep.mutateAsync({ id: step.id, data: { alertConfig: {} } });
    } catch {
      // toast já tratado no hook useUpdateFunnelStep
    } finally {
      if (expandedStepId === step.id) {
        setExpandedStepId(null);
        setStepDraft(null);
        setStepDraftErrors({});
      }
    }
  };

  return (
    <div className="space-y-0" data-testid="funnel-alerts-tab">
      <div className="pb-5">
        <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>
          Avisos visuais no kanban quando um lead fica parado. As regras de atividades continuam
          valendo; os relógios abaixo sobrepõem quando estourados.
        </p>

        <div
          className="flex items-start gap-3 p-3.5 rounded-xl border mt-4"
          style={{ background: 'linear-gradient(180deg,#fbfaff,#f5f2ff)', borderColor: '#eae6fb' }}
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#ffffff', color: '#7c3aed', boxShadow: 'inset 0 0 0 1px rgba(139,92,246,0.10)' }}
          >
            <Icon name="clock" size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>Relógios ativos</div>
            <div className="text-xs mt-0.5" style={{ color: '#64748b' }} data-testid="funnel-alerts-summary">
              {summaryText}
            </div>
          </div>
        </div>
      </div>

      <AlertClockSection
        title="Inatividade (última atividade)"
        description="Dias desde a última atividade registrada no lead."
        state={inactivity}
        onChange={setInactivity}
        error={inactivityError}
        note="Só conta para leads sem atividade futura agendada."
        testidPrefix="funnel-alerts-inactivity"
      />

      <AlertClockSection
        title="Tempo na etapa"
        description="Dias desde que o lead entrou na etapa atual."
        state={timeInStep}
        onChange={setTimeInStep}
        error={timeInStepError}
        testidPrefix="funnel-alerts-time-in-step"
      />

      <div className="py-5 border-t" style={{ borderColor: '#f1f5f9' }}>
        <label className="block text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: '#94a3b8' }}>
          Limites por etapa
        </label>

        {stepsLoading ? (
          <div className="text-center py-6 text-sm" style={{ color: '#64748b' }}>
            Carregando etapas...
          </div>
        ) : steps.length === 0 ? (
          <div className="text-center py-6 text-sm rounded-lg border border-dashed" style={{ color: '#9ca3af', borderColor: '#e5e7eb' }}>
            Adicione etapas para configurar limites específicos por etapa.
          </div>
        ) : (
          <div className="flex flex-col">
            {steps.map((step) => (
              <StepAlertRow
                key={step.id}
                step={step}
                isExpanded={expandedStepId === step.id}
                draft={expandedStepId === step.id ? stepDraft : null}
                errors={expandedStepId === step.id ? stepDraftErrors : {}}
                onTogglePersonalize={() => handleTogglePersonalize(step)}
                onChangeDraft={setStepDraft}
                onSave={() => handleSaveStepOverride(step)}
                onRestoreDefault={() => handleRestoreStepDefault(step)}
                isSaving={updateFunnelStep.isPending}
              />
            ))}
          </div>
        )}
      </div>

      <div className="py-5 border-t" style={{ borderColor: '#f1f5f9' }}>
        <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: '#94a3b8' }}>
          Notificações
        </label>
        <div>
          <NotificationRow
            title="Notificar responsável ao entrar em alerta"
            description="Envia um aviso ao responsável quando o lead atinge o limite de alerta."
            checked={notifications.onWarning}
            onChange={(checked) => setNotifications({ ...notifications, onWarning: checked })}
            testid="funnel-alerts-notif-on-warning"
          />
          <NotificationRow
            title="Notificar responsável e gestor ao entrar em crítico"
            description="Envia um aviso ao responsável e ao gestor do funil quando o lead atinge o limite crítico."
            checked={notifications.onCritical}
            onChange={(checked) => setNotifications({ ...notifications, onCritical: checked })}
            testid="funnel-alerts-notif-on-critical"
          />
          <NotificationRow
            title="Resumo semanal para o gestor"
            description="Consolidado dos leads parados, enviado semanalmente por e-mail. (em breve)"
            checked={notifications.weeklyDigest}
            onChange={(checked) => setNotifications({ ...notifications, weeklyDigest: checked })}
            testid="funnel-alerts-notif-weekly-digest"
            isLast
          />
        </div>
      </div>

      <div className="pt-5 flex items-center justify-end gap-2">
        <Button
          variant="primary"
          onClick={handleSaveFunnelAlerts}
          disabled={!canSaveFunnelAlerts}
          loading={updateFunnel.isPending}
          data-testid="funnel-alerts-save"
        >
          Salvar configurações de alerta
        </Button>
      </div>
    </div>
  );
};

FunnelAlertsTab.displayName = 'FunnelAlertsTab';

// ============================================================================
// AlertClockSection — bloco reutilizado por Inatividade / Tempo na etapa
// ============================================================================

interface AlertClockSectionProps {
  title: string;
  description: string;
  state: ClockFormState;
  onChange: (next: ClockFormState) => void;
  error: string | null;
  note?: string;
  testidPrefix: string;
}

const AlertClockSection: React.FC<AlertClockSectionProps> = ({
  title,
  description,
  state,
  onChange,
  error,
  note,
  testidPrefix,
}) => (
  <div className="py-5 border-t" style={{ borderColor: '#f1f5f9' }}>
    <div className="flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: '#1e293b' }}>{title}</div>
        <div className="text-xs mt-1" style={{ color: '#64748b' }}>{description}</div>
      </div>
      <div data-testid={`${testidPrefix}-toggle`}>
        <Switch
          checked={state.enabled}
          onChange={(checked) => onChange({ ...state, enabled: checked })}
        />
      </div>
    </div>

    {state.enabled && (
      <>
        <div className="flex flex-wrap gap-6 mt-4">
          <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
            Alerta após
            <Input
              type="number"
              min={1}
              value={state.warningDays}
              onChange={(e) => onChange({ ...state, warningDays: Number(e.target.value) })}
              fullWidth={false}
              style={{ width: 68, textAlign: 'center' }}
              data-testid={`${testidPrefix}-warning-days`}
            />
            dias
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
            Crítico após
            <Input
              type="number"
              min={1}
              value={state.criticalDays}
              onChange={(e) => onChange({ ...state, criticalDays: Number(e.target.value) })}
              fullWidth={false}
              style={{ width: 68, textAlign: 'center' }}
              data-testid={`${testidPrefix}-critical-days`}
            />
            dias
          </label>
        </div>

        {error && (
          <p className="text-xs mt-2" style={{ color: '#dc2626' }} data-testid={`${testidPrefix}-error`}>
            {error}
          </p>
        )}
        {note && (
          <p className="text-xs mt-2" style={{ color: '#94a3b8' }}>{note}</p>
        )}
      </>
    )}
  </div>
);

// ============================================================================
// NotificationRow — linha de toggle da seção "Notificações"
// ============================================================================

interface NotificationRowProps {
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testid: string;
  isLast?: boolean;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ title, description, checked, onChange, testid, isLast }) => (
  <div
    className="flex items-center gap-4 py-3"
    style={isLast ? undefined : { borderBottom: '1px solid #f1f5f9' }}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium" style={{ color: '#1e293b' }}>{title}</div>
      <div className="text-xs mt-1" style={{ color: '#64748b' }}>{description}</div>
    </div>
    <div data-testid={testid}>
      <Switch checked={checked} onChange={onChange} />
    </div>
  </div>
);

// ============================================================================
// StepAlertRow — linha de etapa em "Limites por etapa"
// ============================================================================

interface StepAlertRowProps {
  step: FunnelStepResponse;
  isExpanded: boolean;
  draft: StepOverrideDraft | null;
  errors: { inactivity?: string | null; timeInStep?: string | null };
  onTogglePersonalize: () => void;
  onChangeDraft: (draft: StepOverrideDraft) => void;
  onSave: () => void;
  onRestoreDefault: () => void;
  isSaving: boolean;
}

const StepAlertRow: React.FC<StepAlertRowProps> = ({
  step,
  isExpanded,
  draft,
  errors,
  onTogglePersonalize,
  onChangeDraft,
  onSave,
  onRestoreDefault,
  isSaving,
}) => {
  const overridden = hasStepOverride(step);
  const btnLabel = isExpanded ? 'Recolher' : overridden ? 'Editar personalização' : 'Personalizar';

  return (
    <div
      className="py-3 border-t first:border-t-0"
      style={{ borderColor: '#f1f5f9' }}
      data-testid={`funnel-alerts-stage-${step.id}`}
    >
      <div className="flex items-center gap-2.5">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: step.color }} />
        <span className="text-sm font-medium flex-1 min-w-0 truncate" style={{ color: '#1e293b' }}>
          {step.name}
        </span>
        <span
          className="text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 whitespace-nowrap"
          style={
            overridden
              ? { backgroundColor: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' }
              : { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }
          }
        >
          {overridden ? 'Personalizado' : 'Padrão do funil'}
        </span>
        <button
          type="button"
          onClick={onTogglePersonalize}
          className="flex items-center gap-1 text-xs font-semibold flex-shrink-0 whitespace-nowrap"
          style={{ color: '#7c3aed' }}
          data-testid={`funnel-alerts-stage-personalize-${step.id}`}
        >
          <span>{btnLabel}</span>
          <span
            className="inline-flex transition-transform"
            style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}
          >
            <Icon name="chevron-down" size={14} />
          </span>
        </button>
      </div>

      {isExpanded && draft && (
        <div className="mt-3 p-4 rounded-xl" style={{ backgroundColor: '#fafafa', border: '1px solid #e8e8ec' }}>
          <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: '#94a3b8' }}>
            Inatividade nesta etapa
          </label>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              Alerta após
              <Input
                type="number"
                min={1}
                value={draft.inactivity.warningDays}
                onChange={(e) =>
                  onChangeDraft({ ...draft, inactivity: { ...draft.inactivity, warningDays: Number(e.target.value) } })
                }
                fullWidth={false}
                style={{ width: 68, textAlign: 'center' }}
                data-testid={`funnel-alerts-stage-inactivity-warning-${step.id}`}
              />
              dias
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              Crítico após
              <Input
                type="number"
                min={1}
                value={draft.inactivity.criticalDays}
                onChange={(e) =>
                  onChangeDraft({ ...draft, inactivity: { ...draft.inactivity, criticalDays: Number(e.target.value) } })
                }
                fullWidth={false}
                style={{ width: 68, textAlign: 'center' }}
                data-testid={`funnel-alerts-stage-inactivity-critical-${step.id}`}
              />
              dias
            </label>
          </div>
          {errors.inactivity && (
            <p className="text-xs mt-2" style={{ color: '#dc2626' }}>{errors.inactivity}</p>
          )}

          <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2 mt-4" style={{ color: '#94a3b8' }}>
            Tempo nesta etapa
          </label>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              Alerta após
              <Input
                type="number"
                min={1}
                value={draft.timeInStep.warningDays}
                onChange={(e) =>
                  onChangeDraft({ ...draft, timeInStep: { ...draft.timeInStep, warningDays: Number(e.target.value) } })
                }
                fullWidth={false}
                style={{ width: 68, textAlign: 'center' }}
                data-testid={`funnel-alerts-stage-timeinstep-warning-${step.id}`}
              />
              dias
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              Crítico após
              <Input
                type="number"
                min={1}
                value={draft.timeInStep.criticalDays}
                onChange={(e) =>
                  onChangeDraft({ ...draft, timeInStep: { ...draft.timeInStep, criticalDays: Number(e.target.value) } })
                }
                fullWidth={false}
                style={{ width: 68, textAlign: 'center' }}
                data-testid={`funnel-alerts-stage-timeinstep-critical-${step.id}`}
              />
              dias
            </label>
          </div>
          {errors.timeInStep && (
            <p className="text-xs mt-2" style={{ color: '#dc2626' }}>{errors.timeInStep}</p>
          )}

          <div className="flex items-center gap-4 mt-4">
            <Button
              variant="primary"
              onClick={onSave}
              loading={isSaving}
              data-testid={`funnel-alerts-stage-save-${step.id}`}
            >
              Salvar personalização
            </Button>
            {overridden && (
              <button
                type="button"
                onClick={onRestoreDefault}
                className="text-xs font-semibold"
                style={{ color: '#dc2626' }}
                data-testid={`funnel-alerts-stage-restore-${step.id}`}
              >
                Restaurar padrão
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

StepAlertRow.displayName = 'StepAlertRow';
